import type { DropdownUser } from '@/api/capa/types';
import type {
  CollaborationStatus,
  ProductCategory,
  RelationshipType,
  StyleTypePage,
} from '@/api/dropdowns/types';
import type { FacilityProcessGroup } from '@/api/orders/types';
import type { DropdownCompany } from '@/api/product-mapping/types';
import { API_BASE_URL, API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchCollaborationStatuses(): Promise<{
  data: CollaborationStatus[];
}> {
  return apiFetch<{ data: CollaborationStatus[] }>(
    '/v3/collaboration-statuses',
    {},
    API_BASE_URL,
  );
}

export async function fetchRelationshipTypes(): Promise<{
  data: RelationshipType[];
}> {
  return apiFetch<{ data: RelationshipType[] }>(
    '/v3/relationship-types',
    {},
    API_BASE_URL,
  );
}

export async function fetchFacilityProcessOptions(): Promise<{
  facilities: FacilityProcessGroup[];
}> {
  return apiFetch<{ facilities: FacilityProcessGroup[] }>(
    '/v3/facility-processes/drop-down',
    {},
    API_BASE_URL,
  );
}

export async function fetchCoworkerOptions(): Promise<{
  data: DropdownUser[];
}> {
  return apiFetch<{ data: DropdownUser[] }>(
    '/v3/users?isDropDown=true&selectCoWorkers=true',
    {},
    API_BASE_URL,
  );
}

export async function fetchUsersByCompanyIds(
  companyIds: string,
): Promise<DropdownUser[]> {
  return apiFetch<DropdownUser[]>(
    `/v3/user-companies/list-by-company-ids?companyIds=${encodeURIComponent(companyIds)}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchNetworkCompaniesDropdown(): Promise<{
  data: DropdownCompany[];
}> {
  return apiFetch<{ data: DropdownCompany[] }>(
    '/api/companies?isDropDown=true&isSupplierNetwork=true',
    {},
    API_ROOT_URL,
  );
}

export async function fetchAllCompaniesDropdown(): Promise<{
  data: DropdownCompany[];
}> {
  return apiFetch<{ data: DropdownCompany[] }>(
    '/api/companies?isDropDown=true&includeOwnCompany=true',
    {},
    API_ROOT_URL,
  );
}

export async function fetchProductCategories(): Promise<{
  data: ProductCategory[];
}> {
  return apiFetch<{ data: ProductCategory[] }>(
    '/v3/product-categories?isDropDown=true',
    {},
    API_BASE_URL,
  );
}

export async function fetchAllCountries(): Promise<
  Array<{ countryCode: string; countryNameEn: string }>
> {
  const { default: allCountries } = await import('@/lib/constants/countries');
  return allCountries as Array<{ countryCode: string; countryNameEn: string }>;
}

export async function fetchStyleTypeListPage(
  body: object,
): Promise<StyleTypePage> {
  return apiFetch<StyleTypePage>(
    '/style-types/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchParentStyleOptions(
  body: object,
): Promise<{ data: Array<{ id: string; name: string }> }> {
  return apiFetch<{ data: Array<{ id: string; name: string }> }>(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchParentVariantOptions(
  body: object,
): Promise<{ data: Array<{ id: string; name: string }> }> {
  return apiFetch<{ data: Array<{ id: string; name: string }> }>(
    '/style-variants/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchRawMaterials(): Promise<{
  data: Array<{ id: string; name: string }>;
}> {
  return apiFetch<{ data: Array<{ id: string; name: string }> }>(
    '/v3/materials?isDropDown=true',
    {},
    API_BASE_URL,
  );
}
